import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, ArrowRight, Check, Mail } from 'lucide-react';
import { GRAD_TEXT } from './shared';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ExitIntentModal = ({ onStart }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | done | error

  useEffect(() => {
    if (sessionStorage.getItem('exit_intent_shown')) return;
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget && !sessionStorage.getItem('exit_intent_shown')) {
        sessionStorage.setItem('exit_intent_shown', '1');
        setOpen(true);
      }
    };
    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, []);

  const close = useCallback(() => { setOpen(false); setState('idle'); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState('loading');
    try {
      await axios.post(`${API}/leads/capture`, { email, source: 'exit_intent' });
      setState('done');
      setTimeout(close, 2500);
    } catch {
      setState('error');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-intent-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(48,43,44,0.45)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
          }}
        >
          <motion.div
            data-testid="exit-intent-modal"
            initial={{ scale: 0.92, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '460px', background: '#FDF8F3',
              border: '1px solid rgba(48,43,44,0.12)', borderRadius: '2px',
              boxShadow: '0 32px 80px -20px rgba(48,43,44,0.35)',
              padding: '2.5rem', position: 'relative', textAlign: 'center',
            }}
          >
            <button
              data-testid="exit-intent-close"
              onClick={close}
              style={{
                position: 'absolute', top: '14px', right: '14px',
                background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(48,43,44,0.45)',
              }}
            ><X size={18} /></button>

            {state === 'done' ? (
              <div data-testid="exit-intent-success" style={{ padding: '1.5rem 0' }}>
                <span style={{
                  display: 'inline-flex', width: '52px', height: '52px', borderRadius: '50%',
                  alignItems: 'center', justifyContent: 'center', marginBottom: '1rem',
                  background: 'rgba(11,129,90,0.1)', color: '#0B815A',
                }}><Check size={26} /></span>
                <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 800, color: '#302B2C', margin: '0 0 0.5rem' }}>You're on the list</h3>
                <p style={{ fontSize: '0.875rem', color: 'rgba(48,43,44,0.65)', margin: 0 }}>We'll send your free assessment link shortly.</p>
              </div>
            ) : (
              <>
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: '0.65rem', fontWeight: 700,
                  letterSpacing: '0.25em', textTransform: 'uppercase', color: '#F67425',
                }}>Before You Go</span>
                <h3 style={{
                  fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.35rem, 3vw, 1.75rem)',
                  fontWeight: 900, color: '#302B2C', letterSpacing: '-0.03em', lineHeight: 1.1,
                  margin: '0.75rem 0',
                }}>
                  Your migration map takes <span style={GRAD_TEXT}>5 minutes</span>
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'rgba(48,43,44,0.68)', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
                  Not ready right now? Leave your email and we'll save your free assessment for later.
                </p>
                <form onSubmit={submit} style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Mail size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(48,43,44,0.4)' }} />
                    <input
                      data-testid="exit-intent-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      style={{
                        width: '100%', padding: '12px 12px 12px 36px',
                        border: '1px solid rgba(48,43,44,0.2)', borderRadius: '2px',
                        fontFamily: 'var(--font-body)', fontSize: '0.875rem', background: '#fff',
                        outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <button
                    data-testid="exit-intent-submit"
                    type="submit"
                    disabled={state === 'loading'}
                    style={{
                      padding: '12px 20px', flexShrink: 0,
                      background: 'linear-gradient(135deg, #F67425, #BB0518)', color: '#fff',
                      border: 'none', borderRadius: '2px', cursor: 'pointer',
                      fontFamily: 'var(--font-heading)', fontSize: '0.85rem', fontWeight: 700,
                    }}
                  >{state === 'loading' ? '...' : 'Save My Spot'}</button>
                </form>
                {state === 'error' && (
                  <p data-testid="exit-intent-error" style={{ fontSize: '0.75rem', color: '#BB0518', margin: '0 0 0.75rem' }}>Something went wrong — please try again.</p>
                )}
                <button
                  data-testid="exit-intent-start-now"
                  onClick={() => { close(); onStart(); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--font-body)', fontSize: '0.8125rem', fontWeight: 700,
                    color: '#0B815A',
                  }}
                >Or start the free assessment now <ArrowRight size={14} /></button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentModal;
